import { useMemo } from "react";

import type { Point } from "highcharts";
import { renderToString } from "react-dom/server";
import styled from "styled-components";

import { Map } from "@similarweb/pro-logic-components";

import { useMapGeoJson } from "app/common/hooks";
import { TranslationProvider, useTranslation } from "pro-features/components/WithTranslation";
import type { IDurationObject } from "app/services/DurationService";

import { TrafficByCountryMapLegend } from "./TrafficByCountryMapLegend";
import { TrafficByCountryMapTooltip } from "./TrafficByCountryMapTooltip";
import { TrafficByCountryMapTooltipComparison } from "./TrafficByCountryMapTooltipComparison";
import type { ChartSeriesItem } from "./types";

const TrafficByCountryMapContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const MapWrapper = styled.div`
    height: 100%;
`;

interface ITrafficByCountryMapProps {
    duration: IDurationObject<string>;
    isPeriodOverPeriod: boolean;
    isYearOverYear: boolean;
    data: ChartSeriesItem[] | undefined;
}

export const TrafficByCountryMap = ({
    duration,
    isPeriodOverPeriod,
    isYearOverYear,
    data = [],
}: ITrafficByCountryMapProps) => {
    const t = useTranslation();
    const { data: mapGeoJson, isLoading: isMapGeoJsonLoading } = useMapGeoJson();
    const chartConfig = useMemo(
        () => ({
            chart: {
                height: isPeriodOverPeriod ? 200 : 232,
                animation: false,
            },
            title: { text: undefined },
            tooltip: {
                outside: true,
                useHTML: true,
                style: { fontFamily: '"Roboto", sans-serif' },
                formatter: function (this: Point) {
                    const { isComparison, title, tooltipData } = this.options as ChartSeriesItem;

                    return renderToString(
                        <TranslationProvider translate={t}>
                            {isComparison ? (
                                <TrafficByCountryMapTooltipComparison
                                    title={title}
                                    data={tooltipData}
                                />
                            ) : (
                                <TrafficByCountryMapTooltip
                                    title={title}
                                    data={tooltipData}
                                    duration={duration}
                                    isYearOverYear={isYearOverYear}
                                />
                            )}
                        </TranslationProvider>,
                    );
                },
            },
        }),
        [t, duration, isPeriodOverPeriod, isYearOverYear],
    );

    return (
        <TrafficByCountryMapContainer>
            <MapWrapper>
                <Map
                    config={chartConfig}
                    mapData={mapGeoJson}
                    data={data}
                    isLoading={isMapGeoJsonLoading}
                    style={{ height: "100%", width: "100%" }}
                />
            </MapWrapper>
            {isPeriodOverPeriod ? <TrafficByCountryMapLegend /> : null}
        </TrafficByCountryMapContainer>
    );
};
